<template>
  <v-form ref="formRef" v-model="valid" @submit.prevent="submit">
    <!-- Date Input -->
    <v-text-field
      v-model="dateForInput"
      label="Datum"
      type="date"
      prepend-icon="mdi-calendar"
      :rules="[rules.required]"
      variant="outlined"
      density="comfortable"
      class="mb-3"
    ></v-text-field>

    <!-- Time Input -->
    <v-text-field
      v-model="report.time"
      label="Tijd"
      type="time"
      prepend-icon="mdi-clock"
      variant="outlined"
      density="comfortable"
      class="mb-3"
    ></v-text-field>

    <!-- Report Category -->
    <v-select
      v-model="report.report_category"
      :items="categories"
      item-title="title"
      item-value="title"
      label="Type rapport"
      prepend-icon="mdi-format-list-bulleted-type"
      :rules="[rules.required]"
      variant="outlined"
      density="comfortable"
      class="mb-3"
    >
      <template #selection="{ item }">
        <div class="d-flex align-center">
          <v-icon :color="item.raw.color" class="mr-2">{{
            item.raw.icon
          }}</v-icon>
          <span>{{ item.raw.title }}</span>
        </div>
      </template>
      <template #item="{ item, props }">
        <v-list-item v-bind="props">
          <template #prepend>
            <v-icon :color="item.raw.color">{{ item.raw.icon }}</v-icon>
          </template>
          <v-list-item-title>{{ item.raw.title }}</v-list-item-title>
        </v-list-item>
      </template>
    </v-select>

    <!-- Amount for specific types -->
    <v-slider
      v-if="needsAmount"
      v-model="report.amount"
      label="Aantal"
      thumb-label="always"
      :max="10"
      :min="1"
      :step="1"
      :rules="needsAmount ? [rules.required] : []"
      class="mb-4"
    ></v-slider>

    <!-- CAS switches -->
    <template v-if="isCasReport">
      <v-switch
        v-model="report.cas_assessed"
        label="Beoordeeld CD"
        class="mb-2"
      ></v-switch>
      <v-switch
        v-model="report.cas_admitted"
        label="Opgenomen CD"
        class="mb-2"
      ></v-switch>
      <v-switch
        v-model="report.dangerous"
        label="Gevaarlijke situatie"
        class="mb-2"
      ></v-switch>
    </template>

    <!-- Trait selection for new reports -->
    <ReportTraitPicker
      v-if="needsAmount"
      v-model="report.trait_ids"
      :account_id="report.account_id"
      :is-new-report="!report.id"
      class="mb-3"
    />

    <!-- Description -->
    <AtMentionBox
      ref="atMentionBoxRef"
      v-model="report.description"
      :quick-actions="computedQuickActions"
      class="mb-4"
    />

    <!-- AI-check (beta_tester only): one button, suggestions only -->
    <template v-if="canUseAi">
      <v-btn
        block
        variant="tonal"
        color="primary"
        prepend-icon="mdi-creation-outline"
        :loading="aiCheck.checking"
        :disabled="!aiCheck.hasText"
        class="mb-2"
        @click="aiCheck.run()"
      >
        AI-check
      </v-btn>

      <!-- Reopen the results of the last check -->
      <v-btn
        v-if="aiCheck.hasResults && !aiCheck.checking"
        block
        variant="text"
        size="small"
        class="mb-2 text-none"
        @click="aiCheck.show()"
      >
        {{ aiCheckSummary }}
      </v-btn>

      <!-- Tasks the worker agreed to: created when the report is saved -->
      <v-card v-if="aiCheck.queuedTasks.length" variant="outlined" class="mb-3">
        <v-card-text class="py-2">
          <div class="text-caption text-medium-emphasis mb-1">
            {{ queuedTasksLabel }}
          </div>
          <div
            v-for="task in aiCheck.queuedTasks"
            :key="task.id"
            class="d-flex align-center ga-2"
          >
            <v-icon size="small">mdi-calendar-check-outline</v-icon>
            <span class="text-body-2 flex-grow-1">{{ task.description }}</span>
            <v-btn
              icon="mdi-close"
              size="small"
              variant="text"
              :aria-label="`Taak verwijderen: ${task.description}`"
              @click="aiCheck.removeQueuedTask(task.id)"
            />
          </div>
        </v-card-text>
      </v-card>

      <AiDraftCheckSheet
        :check="aiCheck"
        :selected-trait-ids="report.trait_ids || []"
      />
    </template>

    <!-- Submit -->
    <v-btn
      type="submit"
      block
      color="primary"
      :loading="isLoading"
      :disabled="!valid"
      class="mt-4"
    >
      {{ submitButtonText }}
    </v-btn>
  </v-form>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from "vue";
import { useReportsStoreRefactor } from "@/stores/reportsStoreRefactor";
import { useReportCategoriesStoreRefactor } from "@/stores/reportCategoriesStoreRefactor";
import { useReportCategoryIcons } from "@/composables/useReportCategoryIcons";
import { useFormsStore } from "@/stores/formsStore";
import { useAuthStoreRefactor } from "@/stores/authStoreRefactor";
import { useAccountsStoreRefactor } from "@/stores/accountsStoreRefactor";
import { useRouter } from "vue-router";
import { useAiAccess } from "@/composables/useAiAccess";
import { useAiDraftCheck } from "@/composables/useAiDraftCheck";
import AtMentionBox from "../ui/AtMentionBox.vue";
import ReportTraitPicker from "./ReportTraitPicker.vue";
import AiDraftCheckSheet from "../ai/AiDraftCheckSheet.vue";

const props = defineProps({
  report: { type: Object, required: true },
  submitButtonText: { type: String, default: "Opslaan" },
  popup: Boolean,
});

const emit = defineEmits(["update:report", "saved", "report-created"]);

// Stores
const reportsStore = useReportsStoreRefactor();
const reportCategoriesStore = useReportCategoriesStoreRefactor();
const formsStore = useFormsStore();
const authStore = useAuthStoreRefactor();
const accountsStore = useAccountsStoreRefactor();
const router = useRouter();

// Composables
const { getCategoryIcon, getCategoryColor, ensureCategoriesLoaded } =
  useReportCategoryIcons();

// Refs
const formRef = ref(null);
const atMentionBoxRef = ref(null);
const valid = ref(false);

// Form rules
const rules = {
  required: (value) => !!value || "Dit veld is verplicht",
};

// Local report data
const report = ref({ ...props.report });

// Watch props and emit updates
watch(
  () => props.report,
  (newVal) => {
    Object.assign(report.value, newVal);
  },
  { deep: true },
);

watch(
  report,
  (newVal) => {
    emit("update:report", { ...newVal });
  },
  { deep: true },
);

// Computed properties
const isLoading = computed(() => reportsStore.isLoading);

const categories = computed(() => {
  return reportCategoriesStore.reportCategories.map((category) => ({
    title: category.name,
    icon: getCategoryIcon(category.name),
    color: getCategoryColor(category.name),
  }));
});

const computedQuickActions = computed(() => {
  const cat = reportCategoriesStore.reportCategories.find(
    (c) => c.name === report.value.report_category,
  );
  if (cat && cat.quick_action_name && cat.quick_action_template) {
    return [
      { name: cat.quick_action_name, template: cat.quick_action_template },
    ];
  }
  return [];
});

const needsAmount = computed(() => {
  return ["CAS", "CAG", "Communicatie"].includes(report.value.report_category);
});

const isCasReport = computed(() => {
  return report.value.report_category === "CAS";
});

// Handle date formatting for HTML date input
const dateForInput = computed({
  get: () => (report.value.date ? report.value.date.split("T")[0] : ""),
  set: (value) => {
    report.value.date = value;
  },
});

// Methods
const addText = (text) => {
  atMentionBoxRef.value?.addText(text);
};

// --- AI-check ----------------------------------------------------------------
// One button runs every applicable check on the draft (no report id yet). The
// worker agrees to each suggestion; accepted tasks are queued on the draft and
// created after the report is saved. See useAiDraftCheck.
const { canUseAi } = useAiAccess();

const reportCategoryId = computed(
  () =>
    reportCategoriesStore.reportCategories.find(
      (c) => c.name === report.value.report_category,
    )?.id,
);

const aiCheck = reactive(
  useAiDraftCheck({
    report,
    editor: atMentionBoxRef,
    reportCategoryId,
    includeTraits: needsAmount,
  }),
);

const aiCheckSummary = computed(() => {
  const open = aiCheck.sectionList.filter((section) => section.count > 0);
  if (!open.length) return "AI-check bekijken";
  return `AI-check: ${open.map((section) => `${section.title.toLowerCase()} (${section.count})`).join(" · ")}`;
});

const queuedTasksLabel = computed(() =>
  aiCheck.queuedTasks.length === 1
    ? "1 taak wordt aangemaakt bij opslaan"
    : `${aiCheck.queuedTasks.length} taken worden aangemaakt bij opslaan`,
);

const submit = async () => {
  if (!formRef.value.validate()) return;

  try {
    // Queued AI tasks are not part of the report; they are created after it saves.
    const { ai_follow_ups: queuedTasks = [], ...payload } = report.value;
    let result;
    if (payload.id) {
      result = await reportsStore.update(payload);
    } else {
      result = await reportsStore.create(payload);
    }

    if (result) {
      await aiCheck.createQueuedTasks(queuedTasks, payload.account_id);

      emit("saved", result);
      emit("report-created", result);

      if (props.popup) {
        await formsStore.$reset();
      } else {
        router.replace("/");
      }
    }
  } catch (error) {
    console.error("Error submitting report:", error);
  }
};

// Initialize
onMounted(async () => {
  await ensureCategoriesLoaded();

  // Set defaults
  if (!report.value.date) {
    report.value.date = new Date().toISOString().split("T")[0];
  }
  if (!report.value.time) {
    const now = new Date();
    report.value.time = `${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}`;
  }
  if (!report.value.amount) {
    report.value.amount = 1;
  }

  // Set user
  if (!report.value.user_id) {
    if (!authStore.user) await authStore.getCurrentUser();
    if (authStore.user?.id) {
      report.value.user_id = authStore.user.id;
    }
  }

  // Initialize trait_ids based on context
  if (report.value.id) {
    // Editing: preload from report.traits → trait_ids
    if (!report.value.trait_ids && Array.isArray(report.value.traits)) {
      report.value.trait_ids = report.value.traits.map((t) => t.id);
    }
  } else {
    // Creating: start with empty selection (no pre-tagging from account)
    if (!Array.isArray(report.value.trait_ids)) {
      report.value.trait_ids = [];
    }
  }
});
</script>
